import { Injectable } from '@angular/core';

export enum HeatEngineConfigurationTranslation {
  Nothing,
  HeatingWithoutPump,
  HeatingWithPump,
  HeatingWithPumpDomesticHotWaterWithPump,
  HeatingAndDomesticHotWaterWithOnePump,
  DomesticHotWaterWithPump,
  HeatingDomesticHotWaterOnePumpFourWaySplit,
  HeatingDomesticHotWaterTwoPumpFourWaySplit,
  HeatingDomesticHotWaterOnePumpFourWayMono,
  HeatingDomesticHotWaterTwoPumpFourWayMono,
  HeatingDomesticHotWaterOnePumpFourWaySplitWoHeater,
  HeatingDomesticHotWaterTwoPumpFourWaySplitWoHeater,
  HeatingDomesticHotWaterOnePumpFourWayMonoWoHeater,
  HeatingDomesticHotWaterTwoPumpFourWayMonoWoHeater,
  HybdridHeatingDomesticHotWaterOnePumpFourWaySplit,
  HybridHeatingDomesticHotWaterOnePumpFourWayMono,
  IceStorage,
}

export enum DomesticHotWaterConfigurationTranslation {
  Nothing,
  DomesticHotWaterCylinderWithSensor,
  DomesticHotWaterCylinderWithTwoSensors,
  DomesticHotWaterShiftLoadCylinderWithTwoSensors,
  CirculationPumpOnly,
  DomesticHotWaterCylinderWithSensorAndCirculationPump,
  DomesticHotWaterCylinderWithTwoSensorsAndCirculationPump,
  DomesticHotWaterShiftLoadCylinderWithTwoSensorsAndCirculationPump,
  DomesticHotWaterShiftLoadCylinderWithSensor,
  DomesticHotWaterShiftLoadCylinderWithSensorAndCirculationPump,
  DomesticHotWaterHeaterInstantaneousWithComfortFunction,
  DomesticHotWaterWithSwitchedInput,
  DomesticHotWaterSwitchedInputAndCirculationPump,
  DomesticHotWaterCylinderSensorEHeater,
  DomesticHotWaterCylinderTwoSensorEHeater,
  DomesticHotWaterCylinderWithoutSensor,
}

export enum HydraulicSeparatorConfigurationTranslation {
  Nothing,
  OnlyHeating,
  DomesticHotWaterCylinderInFrontOfSeparator,
  DomesticHotWaterCylinderBehindOfSeparator,
  BufferOnlyHeating,
  BufferDomesticHotWaterCylinderInFrontOfSeparator,
  BufferDomesticHotWaterCylinderBehindOfSeparator,
  OnlyHeatingWithoutSensor,
  DomesticHotWaterCylinderInFrontOfSeparatorWithoutSensor,
  DomesticHotWaterCylinderBehindOfSeparatorWithoutSensor,
  BufferOnlyHeatingWithoutSensor,
  BufferDomesticHotWaterCylinderInFrontOfSeparatorWithoutSensor,
  BufferDomesticHotWaterCylinderBehindOfSeparatorWithoutSensor,
  BufferHeatingAndCooling,
  BufferOnlyCooling,
  ExtensionHeatingAndCooling,
  ExternalBivalenceMixer,
}

export enum HeatingCircuitConfigurationTranslation {
  Nothing,
  DirectCircuit,
  MixedCircuitWithoutPump,
  MixedCircuitWithPump,
  MixerCircuitSwimmingPool,
  DirectCircuitWithZoneSupport,
  MixedCircuitWithZoneSupport,
  ThermostatTerminalCircuit,
  DirectCircuitCooling,
  DirectCircuitHeatingCooling,
  MixedCircuitWithPumpCooling,
  MixedCircuitWithPumpHeatingCooling,
}

export enum SolarConfigurationTranslation {
  Nothing,
  Basic,
  SecondaryDifferentialTemperatureControllerWithHeatingSuppression,
  SecondaryDifferentialTemperatureController,
  PreheatingWithTwoStepController,
  AlternatingCylinderChargingWithTwoHeatingWaterBufferCylinders = 5,
}

@Injectable({
  providedIn: 'root',
})
export class HydraulicSchematicCodeExtractorService {
  // this class contains business rules from this presentation:
  // https://docs.google.com/presentation/d/1arFKl_bxa-pdnle_JFwSh50UkEZfnF-j-yyckjJwVcg/edit#slide=id.g4c94a41136_0_119
  // contact person:  Alexander Sabjan

  extractCodeFromVddRead(singleRead: any): string {
    const hydraMatrixStructure = singleRead?.structure
      .find((structure) => structure.structure)
      ?.structure.filter((structure) => structure.structure);
    const hydraMatrixStructuresPieces = hydraMatrixStructure.map((structure) => {
      if (
        structure.path === 'PA_DataRecor/PA_HeatEnginProceData' &&
        structure.structure.length === 16
      ) {
        return (
          '16' +
          this.generateCodeUsingConfigurationTranslation(
            HeatEngineConfigurationTranslation,
            structure?.structure
          )
        );
      }
      if (
        structure.path === 'PA_DataRecor/PA_DomesHotWaterCylinProceData' &&
        structure.structure.length === 6
      ) {
        return this.prefix(
          '6' +
            this.generateCodeUsingConfigurationTranslation(
              DomesticHotWaterConfigurationTranslation,
              structure?.structure
            )
        );
      }
      if (
        structure.path === 'PA_DataRecor/PA_HydraSeparProceData' &&
        structure.structure.length === 2
      ) {
        return this.prefix(
          '2' +
            this.generateCodeUsingConfigurationTranslation(
              HydraulicSeparatorConfigurationTranslation,
              structure?.structure
            )
        );
      }
      if (
        structure.path === 'PA_DataRecor/PA_CentrHeatiCircuProceData' &&
        structure.structure.length === 16
      ) {
        return this.prefix(
          '16' +
            this.generateCodeUsingConfigurationTranslation(
              HeatingCircuitConfigurationTranslation,
              structure?.structure
            )
        );
      }
      if (
        structure.path === 'PA_DataRecor/PA_SolarCircuProceData' &&
        structure.structure.length === 6
      ) {
        return this.prefix(
          '6' +
            this.generateCodeUsingConfigurationTranslation(
              SolarConfigurationTranslation,
              structure?.structure
            )
        );
      }
    });

    return hydraMatrixStructuresPieces.join('');
  }

  private generateCodeUsingConfigurationTranslation(
    configurationTranslationEnum: any,
    structure: any
  ): string {
    return structure
      .map((s3) =>
        this.prefix(
          Object.keys(configurationTranslationEnum)
            .filter((key) => !(parseInt(key, 10) >= 0))
            .indexOf(s3?.structure[0]?.value)
            .toString()
        )
      )
      .join('');
  }

  private prefix(value: string): string {
    return `_${value}`;
  }
}
